import React from 'react';
import { Box, IconButton, CircularProgress } from '@mui/material';
import {
  Videocam,
  VideocamOff,
  Mic,
  MicOff,
  CallEnd,
  Settings,
  ScreenShare,
  StopScreenShare,
  Share,
  BlurOn
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const ControlsContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(3),
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: theme.spacing(1.5),
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  borderRadius: '30px',
  padding: theme.spacing(1.5),
  zIndex: 10,
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.1)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    transform: 'translateX(-50%) translateY(-2px)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.4)'
  }
}));

const VideoControls = ({
  isVideoEnabled,
  isAudioEnabled,
  isScreenSharing,
  isConnected,
  hasHungUp,
  toggleVideo,
  toggleAudio,
  toggleScreenShare,
  onHangUp,
  onOpenSettings,
  sessionId,
  onShareLink,
  isBackgroundBlurEnabled,
  isProcessorLoading,
  processorError,
  onBackgroundBlurToggle
}) => {
  if (!isConnected || hasHungUp) {
    return null;
  }

  return (
    <ControlsContainer>
      <IconButton 
        onClick={toggleVideo} 
        sx={{ 
          backgroundColor: isVideoEnabled ? 'rgba(103, 126, 234, 0.2)' : 'rgba(239, 68, 68, 0.2)', 
          color: isVideoEnabled ? '#6b7eef' : '#ef4444',
          border: `1px solid ${isVideoEnabled ? 'rgba(103, 126, 234, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
          width: 50,
          height: 50,
          borderRadius: '50%',
          transition: 'all 0.3s ease',
          '&:hover': { 
            backgroundColor: isVideoEnabled ? 'rgba(103, 126, 234, 0.3)' : 'rgba(239, 68, 68, 0.3)', 
            transform: 'scale(1.1)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          },
          '&:disabled': { 
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.5)',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
        disabled={!isConnected}
      >
        {isVideoEnabled ? <Videocam /> : <VideocamOff />}
      </IconButton>
      
      <IconButton 
        onClick={toggleAudio} 
        sx={{ 
          backgroundColor: isAudioEnabled ? 'rgba(103, 126, 234, 0.2)' : 'rgba(239, 68, 68, 0.2)', 
          color: isAudioEnabled ? '#6b7eef' : '#ef4444',
          border: `1px solid ${isAudioEnabled ? 'rgba(103, 126, 234, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
          width: 50,
          height: 50,
          borderRadius: '50%',
          transition: 'all 0.3s ease',
          '&:hover': { 
            backgroundColor: isAudioEnabled ? 'rgba(103, 126, 234, 0.3)' : 'rgba(239, 68, 68, 0.3)', 
            transform: 'scale(1.1)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          },
          '&:disabled': { 
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.5)',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
        disabled={!isConnected}
      >
        {isAudioEnabled ? <Mic /> : <MicOff />}
      </IconButton>
      
      <IconButton 
        onClick={onBackgroundBlurToggle} 
        disabled={isProcessorLoading || !isVideoEnabled || !isConnected}
        sx={{ 
          backgroundColor: isBackgroundBlurEnabled ? 'rgba(103, 126, 234, 0.2)' : 'rgba(255,255,255,0.1)',
          color: isBackgroundBlurEnabled ? '#6b7eef' : '#ffffff',
          border: `1px solid ${isBackgroundBlurEnabled ? 'rgba(103, 126, 234, 0.3)' : 'rgba(255,255,255,0.2)'}`,
          width: 50,
          height: 50,
          borderRadius: '50%',
          transition: 'all 0.3s ease',
          '&:hover': { 
            backgroundColor: isBackgroundBlurEnabled ? 'rgba(103, 126, 234, 0.3)' : 'rgba(255,255,255,0.2)',
            transform: 'scale(1.1)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          },
          '&:disabled': { 
            backgroundColor: 'rgba(255,255,255,0.05)',
            color: 'rgba(255,255,255,0.3)',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
      >
        {isProcessorLoading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <BlurOn />}
      </IconButton>
      
      <IconButton 
        onClick={toggleScreenShare} 
        sx={{ 
          backgroundColor: isScreenSharing ? 'rgba(251, 191, 36, 0.2)' : 'rgba(255,255,255,0.1)', 
          color: isScreenSharing ? '#fbbf24' : '#ffffff',
          border: `1px solid ${isScreenSharing ? 'rgba(251, 191, 36, 0.3)' : 'rgba(255,255,255,0.2)'}`,
          width: 50,
          height: 50,
          borderRadius: '50%',
          transition: 'all 0.3s ease',
          '&:hover': { 
            backgroundColor: isScreenSharing ? 'rgba(251, 191, 36, 0.3)' : 'rgba(255,255,255,0.2)', 
            transform: 'scale(1.1)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          },
          '&:disabled': { 
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.5)',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
        disabled={!isConnected}
      >
        {isScreenSharing ? <StopScreenShare /> : <ScreenShare />}
      </IconButton>
      
      <IconButton 
        onClick={onOpenSettings} 
        sx={{ 
          backgroundColor: 'rgba(255,255,255,0.1)', 
          color: '#ffffff',
          border: '1px solid rgba(255,255,255,0.2)',
          width: 50,
          height: 50,
          borderRadius: '50%',
          transition: 'all 0.3s ease',
          '&:hover': { 
            backgroundColor: 'rgba(255,255,255,0.2)',
            transform: 'scale(1.1)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          },
          '&:disabled': { 
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.5)',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
        disabled={!isConnected}
      >
        <Settings />
      </IconButton>
      
      <IconButton 
        onClick={onShareLink} 
        sx={{ 
          backgroundColor: 'rgba(59, 130, 246, 0.2)', 
          color: '#3b82f6',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          width: 50,
          height: 50,
          borderRadius: '50%',
          transition: 'all 0.3s ease',
          '&:hover': { 
            backgroundColor: 'rgba(59, 130, 246, 0.3)',
            transform: 'scale(1.1)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          },
          '&:disabled': { 
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.5)',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
        disabled={!isConnected}
      >
        <Share />
      </IconButton>
      
      <IconButton 
        onClick={onHangUp} 
        sx={{ 
          backgroundColor: 'rgba(239, 68, 68, 0.8)', 
          color: '#ffffff',
          border: '1px solid rgba(239, 68, 68, 0.9)',
          width: 50,
          height: 50,
          borderRadius: '50%',
          transition: 'all 0.3s ease',
          '&:hover': { 
            backgroundColor: 'rgba(220, 38, 38, 0.9)',
            transform: 'scale(1.1)',
            boxShadow: '0 4px 20px rgba(239, 68, 68, 0.4)'
          },
          '&:disabled': { 
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.5)',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
        disabled={!isConnected}
      >
        <CallEnd />
      </IconButton>
    </ControlsContainer>
  );
};

export default VideoControls; 